import { RefreshCw } from 'lucide-react';
import type { ReleaseChange } from '@shared/release-notes';
import { EmptyState } from '@/components/EmptyState';
import { ExternalLink } from '@/components/ExternalLink';
import { LoadingState } from '@/components/LoadingState';
import { RecordDialog } from '@/components/RecordDialog';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/formatters';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Newest first; null while loading. */
  changes: ReleaseChange[] | null;
  /** owner/name on GitHub, for the PR and doc links. */
  repo: string | null;
  /** Where a task number opens (the factory project's Board), or null to show it as text. */
  taskHref: (ref: string) => string | null;
  /** Every commit since the open version, on GitHub (shown under the list), or null. */
  compareHref?: string | null;
  /** The list may not go back as far as the open version: says so above the GitHub link. */
  incomplete?: boolean;
  /** Reloads into the new version; with it, the footer has Update now. */
  onUpdate?: () => void;
};

/**
 * The update banner's "What's new" (T-16): the pull requests merged since the running version, each
 * with its summary and links to its PR, tasks and the docs it changed. Links open in a new tab, so
 * reading them doesn't reload this one.
 */
export function WhatsNewDialog({
  open,
  onClose,
  changes,
  repo,
  taskHref,
  compareHref = null,
  incomplete = false,
  onUpdate,
}: Props) {
  return (
    <RecordDialog
      open={open}
      onClose={onClose}
      size="md"
      title="What's new"
      description="What changed since the version you have open."
      errorTitle="Couldn't show what's new"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {onUpdate ? (
            <Button onClick={onUpdate}>
              <RefreshCw className="size-4" />
              Update now
            </Button>
          ) : null}
        </>
      }
    >
      {changes === null ? (
        <LoadingState inline label="Loading what's new…" />
      ) : changes.length === 0 ? (
        <EmptyState
          size="compact"
          title="No list of changes for this update"
          description="It may be a fix pushed straight to main, or the build couldn't read its history."
        />
      ) : (
        <ol className="space-y-5">
          {changes.map((change) => (
            <WhatsNewItem key={change.sha} change={change} repo={repo} taskHref={taskHref} />
          ))}
        </ol>
      )}
      {changes !== null && (compareHref || (incomplete && changes.length > 0)) ? (
        <p className="mt-5 border-t pt-3 text-xs text-muted-foreground">
          {incomplete && changes.length > 0 ? 'Earlier changes may not be listed. ' : null}
          {compareHref ? (
            <ExternalLink href={compareHref} size="xs">
              Every commit since your version, on GitHub
            </ExternalLink>
          ) : null}
        </p>
      ) : null}
    </RecordDialog>
  );
}

function WhatsNewItem({
  change,
  repo,
  taskHref,
}: {
  change: ReleaseChange;
  repo: string | null;
  taskHref: (ref: string) => string | null;
}) {
  const github = repo ? `https://github.com/${repo}` : null;
  const prHref = github && change.pr ? `${github}/pull/${change.pr}` : null;
  const commitHref = github ? `${github}/commit/${change.sha}` : null;
  return (
    <li className="space-y-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h3 className="text-sm font-medium">{change.title}</h3>
        <span className="text-xs text-muted-foreground">{formatDate(change.merged_at)}</span>
      </div>
      {change.summary.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
          {change.summary.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {change.tasks.map((ref) => {
          const href = taskHref(ref);
          return href ? (
            <ExternalLink key={ref} href={href} size="xs">
              {ref}
            </ExternalLink>
          ) : (
            <span key={ref} className="text-muted-foreground">
              {ref}
            </span>
          );
        })}
        {prHref ? (
          <ExternalLink href={prHref} size="xs">
            PR #{change.pr}
          </ExternalLink>
        ) : commitHref ? (
          <ExternalLink href={commitHref} size="xs">
            Commit {change.sha.slice(0, 7)}
          </ExternalLink>
        ) : null}
        {github
          ? change.docs.map((path) => (
              <ExternalLink key={path} href={`${github}/blob/${change.sha}/${path}`} size="xs" variant="muted">
                {path}
              </ExternalLink>
            ))
          : null}
      </div>
    </li>
  );
}
